import {
    deleteDoc,
    doc,
    type DocumentReference,
    type DocumentSnapshot,
    getDoc,
    getDocFromCache,
    onSnapshot,
    setDoc,
    updateDoc,
} from 'firebase/firestore';
import { reaction } from 'mobx';

import { getAppBroker } from 'stores/AppBroker';
import { requireMatchAnyDocument } from 'stores/DatabasePaths';
import { DocumentCollectionType } from 'types/DocumentCollectionType';
import { DocumentEvents } from 'types/DocumentEvents';
import { DocumentStatus } from 'types/DocumentStatus';
import { DocumentType } from 'types/DocumentType';
import { type ProcessingStatus } from 'types/ProcessingStatus';
import { deepCopyForFirestore } from 'utils/Copy';
import DisposerManager from 'utils/DisposerManager';
import { nowTimestamp } from 'utils/FirestoreUtils';
import { hasOwnProperty } from 'utils/Lang';
import { stripOptionalSuffix } from 'utils/String';

import type BreadcrumbManager from './BreadcrumbManager';
import DocumentCollection from './DocumentCollection';
import Store, { type BecomeObservableOptions } from './Store';

/**
 * @class DocumentStore
 * @description DocumentStore is a wrapper class that manages the lifecycle of a Firestore document,
 * and provides abstractions for other additional features for member management, and publicly
 * accessible meta data.
 */
export default class DocumentStore<
    Schema extends object = any,
    PublicSchema extends object = any,
> extends Store {
    protected readonly disposer = new DisposerManager();

    /**
     * An instance of the document's schema class, describing the Firestore
     * document's fields and their default values. Its keys decide which
     * fields `_update` copies into `data`, falling back to `defaults` (which
     * is this, unless overridden) for any the incoming data lacks. `reset`
     * also starts `data` from a copy of it.
     *
     * Assigned by subclasses as a field (`schema = new XSchema()`), so it is
     * still undefined while this class's constructor runs, and stays
     * undefined for stores that don't assign one (e.g. RootStore).
     */
    schema: Schema;

    /**
     * Optional. Describes the public child document (`public/info`) that
     * `savePublicProps` writes on every save. Its keys must be a subset of
     * `schema`'s: values are taken from `data`, and a key whose value there
     * has a different type than here is left out of the write.
     */
    publicSchema: PublicSchema;

    type: DocumentType = DocumentType.DEFAULT;
    collectionType: DocumentCollectionType = DocumentCollectionType.DEFAULT;

    data: Schema = {} as Schema; // Current data for the document.
    oldData: Schema = {} as Schema; // Used internally as a reference to check against updates coming in from the Firestore server.
    newData: Schema = {} as Schema; // Used internally as a reference to check against updates coming in from the Firestore server.

    status: DocumentStatus | ProcessingStatus = DocumentStatus.NONE; // Current document lifecucle status: loaded, loading, error, local
    breadcrumbs: BreadcrumbManager;

    // Most often the DocumentCollection holding this document. It varies by
    // subclass, so it's `unknown` here; a subclass that reads it narrows it
    // with `declare parent: X | undefined`, where X is what it is actually
    // constructed with - check the construction sites rather than trusting
    // the constructor's parameter type, which has been wrong before.
    // `| undefined` because reset() clears it.
    //
    // Reaching a document's surroundings through `parent` names the right
    // object, where asking RootStore for whatever is on screen only does
    // while exactly one is loaded. `declare` is type-only and emits nothing,
    // so becomeObservable() sees the same own properties either way.
    parent: unknown;
    exists = false;
    docRef: DocumentReference; // Direct reference to the Firestore document via the Firestore API.
    publicDocRef: DocumentReference; // Reference to the public child document that contains public metadata about this document.

    lastModified: number = NaN; // Unix Timestamp of the last modified date.
    lastSynced: number = NaN; // Unix Timestamp of the last time this document synced with the server.

    children: DocumentCollection<any, any>; // Reference to an instance of DocumentCollection that contains child documents of this document.

    isViewingComments = false; // Are the comments of this document currently being viewed.
    isRead = true; // Has this document been viewed since it's last update.
    isLocalOnly = false; // Does this document only exist on the local machine.
    isDataLoaded = false; // Is the data for this document fully loaded.

    mounted = false;

    updatesEnabled = true;

    /**
     * @returns {string} The id of the document if the document exists, otherwise it returns an empty string.
     */
    get id() {
        return this.docRef ? this.docRef.id : '';
    }

    /**
     * @returns {string} The path of the document if the document exists, otherwise it returns an empty string.
     */
    get path() {
        return this.docRef ? this.docRef.path : '';
    }

    /**
     * @returns {string} The key of the document. The key is a combination of the document type and the document id.
     */
    get key() {
        return `${this.type}.${this.id}`;
    }
    /**
     * @returns The default values for the document. This is the same as the schema.
     */
    public getDefaults(): Schema {
        return this.schema;
    }

    /**
     * If the parent or docRef parameters are passed in, then assign them
     * to the corresponding properties of the class.
     *
     * @param parent - The parent of this document. Most often an instance of DocumentCollection.
     * @param docRef - The Firestore DocumentReference that this document is connected to.
     */
    constructor(parent?: unknown, docRef?: DocumentReference) {
        // The `data?: Schema` parameter was removed - it was never passed
        // in practice, and both branches of the `if` statement that
        // checked for its presence were effectively dead. In both cases,
        // the reasons relate to the constructor (and its super()) calls
        // running before fields are initialized.
        //
        // - `if (data)` - the case where `data` is passed
        //   - `this._update(data)` - this would not do anything due to
        //     `this.schema` being undefined at this point.
        // - `else` - `data` not passed
        //   - `this.data = Object.assign({}, this.schema)` - effectively
        //      sets `this.data` to `{}`, which matches what the field
        //      initializer already sets.

        super();
        if (parent) this.parent = parent;
        if (docRef) this.connectDocRef(docRef);
    }

    /**
     * Keeps `disposer` out of MobX. See the note on `neverObserve` in Store.ts
     * for why a base class has to do this by override.
     */
    becomeObservable(options?: BecomeObservableOptions) {
        super.becomeObservable({
            except: [...(options?.except ?? []), 'disposer'],
        });
    }

    /** @returns {boolean} Returns true if the document is currently loading. */
    get loading() {
        return this.status === DocumentStatus.LOADING;
    }

    /** @returns {boolean} Returns true if the document has completely loaded. */
    get loaded() {
        return this.status === DocumentStatus.LOADED;
    }

    /** @returns {boolean} Returns true if the document has changed. */
    get changed() {
        return this.lastModified > this.lastSynced;
    }

    /** @returns {string} Returns the Firestore path of the document. */
    get context() {
        return this.path;
    }

    /**
     * Connects the `docRef` and `publicDocRef` properties of this class to Firestore document references.
     * @param {DocumentReference} docRef - The Firestore DocumentReference that this document is connected to.
     * @returns {void}
     * */
    connectDocRef = (docRef: DocumentReference) => {
        this.docRef = docRef;
        this.publicDocRef = doc(this.docRef, 'public/info');
    };

    /**
     * Subscribes to updates of this document in Firestore, applying each
     * snapshot via `onSnapshot`. Resolves once the first snapshot has been
     * applied, whether or not the document exists.
     * */
    loadDataWithUpdates = () => {
        console.log('Document: loadDataWithUpdates');
        return new Promise<void>((resolve) => {
            let first = true;
            this.disposer.add(
                onSnapshot(this.docRef, (snapshot) => {
                    this.onSnapshot(snapshot);
                    if (first) {
                        first = false;
                        resolve();
                    }
                })
            );
        });
    };

    /**
     * Sets the data of this document.
     * @param {Schema} data - The data to be used to update the document.
     * */
    set = (data: Schema) => {
        this._update(data);
    };

    /**
     * load() loads a document's data from Firestore. It is for documents loaded on their own: children
     * created by a DocumentCollection don't go through it, and instead receive their data through the
     * constructor and `_update`.
     * Example:
     * ```
     * let doc = new DocumentStore(undefined, docRef);
     * await doc.load();
     * ```
     * @returns {Promise<Schema>} Returns a promise that resolves with the data of the document.
     * */
    async load() {
        console.log('Document: load', this.constructor.name);
        this.status = DocumentStatus.LOADING;
        // Self-update (own onSnapshot) unless a DocumentCollection parent is
        // already feeding this document its updates, or the owner has opted
        // it out via updatesEnabled.
        const selfUpdating =
            this.updatesEnabled && !(this.parent instanceof DocumentCollection);

        let loadedData = this.data;
        if (this.docRef) {
            if (selfUpdating) {
                await this.loadDataWithUpdates();
                loadedData = this.data;
            } else {
                loadedData = await this.loadData();
            }
        }

        Object.entries(this.data as any).forEach(([key, value]) => {
            if (key === 'type') this.type = value as DocumentType;
        });

        await this.onLoad(this.data);
        this.disposer.add(
            reaction(
                () => this.data,
                () => {
                    console.log('Document: data changed');
                    this.dispatch(DocumentEvents.CHANGED);
                }
            )
        );
        this.onReady();
        this.mounted = true;
        this.status = DocumentStatus.LOADED;

        return loadedData;
    }

    /** A stub method for child classes to override. This will be called after the document loads. The data argument will provide the data that was loaded.
     * @returns {Promise<void>} Promise that resolves when the document has finished loading.
     * */
    onLoad = async (_data: Schema) => {
        // This is a place holder for child classes to override.
    };

    /** A stub method for child classes to override. This will be called after the document is created.
     * A document is created when it is created locally.
     * The data argument will provide the data that was created locally. */
    onCreate = (_data: Schema) => {};

    /** A stub method for child classes to override. This will be called last after a document is fully loaded. */
    onReady = () => {};

    /**
     * Loads the data of this document from Firestore. This method is called by the `load` method.
     * @returns {Promise<Schema>} Returns a promise that resolves with the data of the document.
     * */
    loadData = async () => {
        console.log('Document: loadData');
        let response: DocumentSnapshot | undefined;
        try {
            response = await getDoc(this.docRef);
            const exists = response.exists();
            if (!exists) {
                console.warn(
                    `Document: loadData: Error loading document type '${this.type}`
                );
                return { ...this.schema, type: DocumentType.NONE } as Schema; // TODO: check why this was put here
            }
            const data = response.data() as Schema;
            this.exists = exists;

            this._update(data);
            return data as Schema;
        } catch (e) {
            console.warn(
                `Document: loadData: Error loading document type '${this.type}: Error: ${e}`
            );
            return { ...this.schema, type: DocumentType.NONE } as Schema;
        }
    };
    /**
     * This method is called when the document is updated in Firestore. It is called by the `loadDataWithUpdates` method.
     * @param {DocumentSnapshot} snapshot The snapshot of the document.
     * */
    onSnapshot = (snapshot: DocumentSnapshot) => {
        // Used to be (!snapshot.exists) - the lack of call parens meant it
        // never ran, and the negation squashed the TS2774 that you'd
        // normally get. after removing the bang and swapping the branches,
        // the bug is revealed
        if (snapshot.exists()) {
            // _update sets isDataLoaded on its first call for self-updating
            // stores, which are the only ones that get here
            this._update(snapshot.data() as Schema);
            this.exists = true;
        } else {
            this.status = DocumentStatus.ERROR;
            console.log('Document: onSnapshot: Document does not exist.');
        }
    };

    /**
     * Updates the local data of the document. This method is called by the `update` method.
     * This method is used to update the local data of the document.
     * @param {Partial<Schema>} data The data to update the document with.
     * @returns {Schema} Returns the updated data.
     * */
    updateLocal = (data: Partial<Schema>) => {
        const oldData = Object(this.data);
        for (const key in oldData) {
            if (hasOwnProperty(data, key)) {
                Object(this.data)[key] = Object(data)[key];
            } else {
                // ??? this.data is the same as oldData because of the line above
                Object(this.data)[key] = Object(oldData)[key];
            }
        }
        return this.data;
    };

    /**
     * Updates the local data of the document. This method is called by the `loadData` method.
     * If any data is missing from the document, the default values will be used.
     * @param {Schema} data The data to update the document with.
     * */
    _update = (data: Schema) => {
        this.oldData = this.newData;
        this.newData = data;

        if (!this.schema) {
            console.warn(
                `Document: _update: Schema is not defined for ${this.constructor.name}`,
                this
            );
        }

        // TODO: these few lines could be clarified further
        const schema = Object(this.schema);
        const defaults = Object(this.getDefaults());
        for (const key in schema) {
            if (hasOwnProperty(data, key)) {
                Object(this.data)[key] = Object(data)[key];
            } else {
                Object(this.data)[key] = defaults[key];
            }
        }

        this.lastSynced = new Date().getTime();
        if (this.isDataLoaded || this.parent instanceof DocumentCollection) {
            this.onUpdated(this.newData, this.oldData);
            this.dispatch(DocumentEvents.CHANGED);
        } else {
            this.isDataLoaded = true;
        }
    };

    /**
     * Updates the dateModified property of the document to the current
     * time, if it is present. The document is also saved to Firestore.
     */
    updateDateModified = async () => {
        const data: unknown = this.data;
        if (data && typeof data === 'object' && 'dateModified' in data) {
            data.dateModified = nowTimestamp();
            await this.save();
        }
    };

    /**
     * Sets the name property of the document, if it is present. The
     * document is also saved to Firestore.
     */
    updateName = async (newName: string) => {
        const logPrefix = `[${this.constructor.name}.updateName]`;
        const data: unknown = this.data;
        if (!data) {
            console.warn(`${logPrefix} .data is null/undefined`);
            return;
        }

        if (typeof data !== 'object') {
            console.warn(`${logPrefix} .data is not an object`);
            return;
        }

        if (!('name' in data)) {
            console.warn(`${logPrefix} .data does not have a 'name' property`);
            return;
        }

        data.name = newName;
        await this.save();
    };

    /**
     * This method is a stub to be overriden by the child class. It is called when the document is updated.
     * @param {Schema} _data The new data of the document.
     * @param {Schema} _oldData The old data of the document.
     * */
    onUpdated = (_data: Schema, _oldData: Schema) => {};

    /**
     * This method is a stub to be overriden by the child class. It is called before the document is saved to Firestore.
     * @param {Schema} _data The data of the document to be saved.
     * */
    onBeforeSave = async (_data: Schema) => {};

    /**
     * Saves the document to Firestore.
     * */
    save = async () => {
        await this.saveData();
    };

    /**
     * Updates the document with new data.
     * @param {Schema} newData The new data to update the document with.
     * */
    update = async (newData: Schema) => {
        this._update(newData);
    };

    /**
     * Saves the document to Firestore along with the public metadata.
     * It also calls the `onBeforeSave` method so child classes can do any pre-processing before saving.
     * If this document was created locally, it will create a new document in Firestore.
     * */
    saveData = async () => {
        this.status = DocumentStatus.LOADING;
        await this.onBeforeSave(this.data);

        const payload = deepCopyForFirestore(this.data);

        let isDocNewlyCreated = false;

        try {
            await getDocFromCache(this.docRef);
        } catch {
            isDocNewlyCreated = true;
        }

        if (this.isLocalOnly || isDocNewlyCreated) {
            console.log(
                'Document(' +
                    this.constructor.name +
                    '): saveData: Creating new document.'
            );
            await setDoc(this.docRef, payload);
        } else {
            await updateDoc(this.docRef, payload);
        }

        if (this.publicSchema) await this.savePublicProps();
        this.status = DocumentStatus.LOADED;
    };

    /**
     * Sets whether the user is viewing comments or not.
     * @param {boolean} isViewing Whether the user is viewing comments or not.
     * */
    setViewingComments(isViewing: boolean) {
        this.isViewingComments = isViewing;
    }

    /**
     * Resets the parent most document to its default state.
     * */
    reset() {
        console.log('Document: reset');
        this.onReset();
        this.disposer.clear();
        this.status = DocumentStatus.NONE;
        this.isDataLoaded = false;
        this.parent = undefined;
        this.exists = false;
        this.mounted = false;
        this.lastModified = NaN;
        this.lastSynced = NaN;
        this.isViewingComments = false;
        this.isRead = true;
        this.isLocalOnly = false;
        this.isDataLoaded = false;
        this.updatesEnabled = true;

        this.data = Object.assign({}, this.schema);
    }

    /**
     * Called when the document is reset. To be overridden by child classes.
     * */
    onReset = () => {};

    /**
     * Saves the public metadata of the document to Firestore.
     * */
    savePublicProps = async () => {
        this.status = DocumentStatus.LOADING;
        const publicSchema: Record<string, unknown> = Object(this.publicSchema);
        const currentData: Record<string, unknown> = Object(this.data);
        const publicDataPayload: Record<string, unknown> = {};

        for (const [key, schemaValue] of Object.entries(publicSchema)) {
            const currentDataValue = currentData[key];
            if (typeof schemaValue !== typeof currentDataValue) {
                console.warn(
                    `AdHub: ${key} is not a valid field for public properties`
                );
                continue;
            }
            publicDataPayload[key] = deepCopyForFirestore(currentDataValue);
        }

        publicDataPayload.type = this.type;

        await setDoc(this.publicDocRef, publicDataPayload);
        this.status = DocumentStatus.LOADED;
    };

    /**
     * Deletes the document from Firestore.
     * @returns {Promise<void>} A promise that resolves when the document is deleted.
     * */
    delete = async () => {
        return await deleteDoc(this.docRef);
    };

    /**
     * Sets the document's loading `status` to DocumentStatus.LOADING if isLoading is true, otherwise sets it to DocumentStatus.LOADED.
     * @param {boolean} isLoading Whether the document is loading.
     * */
    setLoading = (isLoading: boolean) => {
        if (isLoading) this.status = DocumentStatus.LOADING;
        else this.status = DocumentStatus.LOADED;
    };

    navigateTo = (e?: React.MouseEvent<HTMLAnchorElement>) => {
        e?.preventDefault();

        // Breadcrumb paths point to the public info instead of the main
        // document; I don't know how widespread this tendency is. Instead
        // of setting up a bunch more PathTemplates to allow the matching
        // of this suffix, just strip it before testing.
        const path = stripOptionalSuffix(this.path, '/public/info');

        const params = requireMatchAnyDocument(path);
        void getAppBroker().navigate(params, { documentTypeHint: this.type });
    };
}
